/**
 * Cart drawer (side cart)
 * ------------------------
 * Self-contained, vanilla JS. No build step, no jQuery dependency.
 *
 * Responsibilities:
 *   - Open / close the drawer (header cart icon, close button, overlay, ESC).
 *   - Open + refresh whenever something is added to the cart. We piggy-back on
 *     the theme's own `cartcount:update` window event, which Empire fires from
 *     its add-to-cart flow (AddToCartFlyout) and we also re-fire on our own
 *     edits so the header count stays in sync.
 *   - Handle quantity +/- and removal from inside the drawer using Shopify's
 *     standard /cart/change.js AJAX endpoint (line-item key based).
 *   - Re-render the body + footer via the Section Rendering API
 *     (?section_id=cart-drawer-ajax) so prices, discounts and totals come
 *     straight from Liquid — no client-side money formatting to drift.
 */
(function () {
  'use strict';

  var SELECTORS = {
    drawer: '[data-cart-drawer]',
    panel: '[data-cart-drawer-panel]',
    overlay: '[data-cart-drawer-overlay]',
    body: '[data-cart-drawer-body]',
    footer: '[data-cart-drawer-footer]',
    freeShipping: '[data-cart-drawer-free-shipping]',
    subtotal: '[data-cart-drawer-subtotal]',
    count: '[data-cart-drawer-count]',
    discounts: '[data-cart-drawer-order-discounts]',
    close: '[data-cart-drawer-close]',
    toggle: '[data-cart-drawer-toggle]',
    item: '[data-cart-drawer-item]',
    key: 'data-cart-drawer-key',
    remove: '[data-cart-drawer-remove]',
    qtyMinus: '[data-cart-drawer-qty-minus]',
    qtyPlus: '[data-cart-drawer-qty-plus]',
    qtyInput: '[data-cart-drawer-qty-input]'
  };

  var OPEN_CLASS = 'cart-drawer--open';
  var BODY_OPEN_CLASS = 'cart-drawer-open';

  function CartDrawer(root) {
    this.root = root;
    this.panel = root.querySelector(SELECTORS.panel);
    this.overlay = root.querySelector(SELECTORS.overlay);
    this.bodyEl = root.querySelector(SELECTORS.body);
    this.footerEl = root.querySelector(SELECTORS.footer);
    this.freeShippingEl = root.querySelector(SELECTORS.freeShipping);
    this.subtotalEl = root.querySelector(SELECTORS.subtotal);
    this.countEl = root.querySelector(SELECTORS.count);
    this.discountsEl = root.querySelector(SELECTORS.discounts);
    this.emptyClass = 'cart-drawer--empty';

    this.isOpen = false;
    this.isUpdating = false;
    // Set while we re-fire cartcount:update from our own edits, so the
    // window listener doesn't try to re-open / re-fetch redundantly.
    this.internalUpdate = false;
    this.lastFocused = null;
    this.routes = (window.Theme && window.Theme.routes) || {};

    this._onKeydown = this._onKeydown.bind(this);

    this._bind();
  }

  CartDrawer.prototype._bind = function () {
    var self = this;

    // Open from any cart toggle (header cart icon, etc.)
    // Skip on the cart page — the full cart UI is already visible there.
    document.addEventListener('click', function (event) {
      var toggle = event.target.closest(SELECTORS.toggle);
      if (toggle) {
        event.preventDefault();
        if (!document.body.classList.contains('template-cart')) self.open();
      }
    });

    // Close interactions
    this.root.addEventListener('click', function (event) {
      if (event.target.closest(SELECTORS.close) || event.target === self.overlay) {
        event.preventDefault();
        self.close();
      }
    });

    // Delegated controls inside the (re-rendered) body
    this.bodyEl.addEventListener('click', function (event) {
      var removeBtn = event.target.closest(SELECTORS.remove);
      if (removeBtn) {
        event.preventDefault();
        self._changeLine(removeBtn, 0);
        return;
      }

      var minus = event.target.closest(SELECTORS.qtyMinus);
      if (minus) {
        event.preventDefault();
        self._step(minus, -1);
        return;
      }

      var plus = event.target.closest(SELECTORS.qtyPlus);
      if (plus) {
        event.preventDefault();
        self._step(plus, 1);
      }
    });

    this.bodyEl.addEventListener('change', function (event) {
      var input = event.target.closest(SELECTORS.qtyInput);
      if (!input) return;
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      self._changeLine(input, qty);
    });

    // Discount code: apply button + Enter key. Delegated on the (static) footer.
    this.footerEl.addEventListener('click', function (event) {
      var applyBtn = event.target.closest('[data-discount-apply]');
      if (!applyBtn) return;
      event.preventDefault();
      self._applyDiscount(applyBtn.getAttribute('data-discount-apply'));
    });

    this.footerEl.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var input = event.target.closest('[data-discount-apply], .cart-drawer__discount-input');
      if (!input || !input.classList.contains('cart-drawer__discount-input')) return;
      event.preventDefault();
      self._applyDiscount(input.id);
    });

    // The theme fires this after add-to-cart; we also re-fire it on our edits.
    window.addEventListener('cartcount:update', function (event) {
      if (self.internalUpdate) return;

      // Refresh contents so the drawer is current, then surface it.
      // Don't auto-open on the dedicated cart page (its own UI handles edits).
      var onCartPage = document.body.classList.contains('template-cart');
      self.refresh().then(function () {
        if (!onCartPage) self.open();
      });
    });

    // Nosto recommendation widgets add to cart via their own client (nostojs),
    // bypassing the Empire ATC flyout entirely. Hook in here so the drawer
    // opens instead of Nosto's native cart notification.
    self._bindNosto();
  };

  /* ----------------------------------------------------------- open / close */

  CartDrawer.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.lastFocused = document.activeElement;
    this.root.classList.add(OPEN_CLASS);
    this.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_OPEN_CLASS);
    document.addEventListener('keydown', this._onKeydown);
    var self = this;
    // Wait for the panel to be visible before focusing it.
    // preventScroll stops the browser scrolling the (overflow:hidden) container
    // to the off-screen panel, which otherwise fights the slide-in animation.
    window.requestAnimationFrame(function () {
      if (!self.panel) return;
      try {
        self.panel.focus({ preventScroll: true });
      } catch (e) {
        self.panel.focus();
      }
      // Belt-and-braces: undo any focus-induced horizontal scroll on the container.
      self.root.scrollLeft = 0;
    });
  };

  CartDrawer.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.classList.remove(OPEN_CLASS);
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_OPEN_CLASS);
    document.removeEventListener('keydown', this._onKeydown);
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  };

  CartDrawer.prototype._onKeydown = function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.close();
    }
  };

  /* --------------------------------------------------------- quantity edits */

  // Bump the quantity input for a +/- button then commit the change.
  CartDrawer.prototype._step = function (button, delta) {
    var item = button.closest(SELECTORS.item);
    if (!item) return;
    var input = item.querySelector(SELECTORS.qtyInput);
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = 0;
    var next = Math.max(0, current + delta);
    this._changeLine(button, next);
  };

  // Commit a quantity (0 = remove) for the line item the control belongs to.
  CartDrawer.prototype._changeLine = function (el, quantity) {
    var item = el.closest(SELECTORS.item);
    if (!item) return;
    var key = item.getAttribute(SELECTORS.key);
    if (!key) return;
    this._change(key, quantity, item);
  };

  CartDrawer.prototype._change = function (key, quantity, itemEl) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.root.classList.add('cart-drawer--loading');
    if (itemEl && quantity === 0) itemEl.classList.add('cart-drawer-item--removing');

    var self = this;
    var changeUrl = (this.routes.cart_change_url || '/cart/change') + '.js';

    fetch(changeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        self._notify(cart);
        return self.refresh();
      })
      .catch(function () {
        if (itemEl) itemEl.classList.remove('cart-drawer-item--removing');
      })
      .then(function () {
        self.isUpdating = false;
        self.root.classList.remove('cart-drawer--loading');
      });
  };

  // Re-fire the theme's cart count event so the header badge updates, without
  // tripping our own open/refresh listener.
  CartDrawer.prototype._notify = function (cart) {
    this.internalUpdate = true;
    window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
    this.internalUpdate = false;
  };

  // Apply a discount code without a page reload: hitting /discount/<code>
  // stores the code against the cart server-side, then we just re-render the
  // drawer so the order discount + new subtotal appear in place.
  CartDrawer.prototype._applyDiscount = function (inputId) {
    if (this.isUpdating) return;
    var input = document.getElementById(inputId);
    var code = input ? input.value.trim() : '';
    if (!code) return;

    var self = this;
    this.isUpdating = true;
    this.root.classList.add('cart-drawer--loading');

    fetch('/discount/' + encodeURIComponent(code), {
      headers: { Accept: 'text/html' }
    })
      .then(function () {
        return self.refresh();
      })
      .then(function () {
        // Subtotal changed — keep any other cart listeners (header, etc.) in sync.
        return fetch((self.routes.cart_url || '/cart') + '.js', {
          headers: { Accept: 'application/json' }
        });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        self._notify(cart);
      })
      .catch(function () {
        /* invalid code / network — drawer simply shows no new discount */
      })
      .then(function () {
        self.isUpdating = false;
        self.root.classList.remove('cart-drawer--loading');
      });
  };

  /* -------------------------------------------------------------- rendering */

  // Pull fresh body + footer markup from the Section Rendering API and swap.
  CartDrawer.prototype.refresh = function () {
    var self = this;
    var root = (this.routes.root_url || '/').replace(/\/$/, '');
    var url = root + '/?section_id=cart-drawer-ajax';

    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newBody = doc.querySelector('[data-html="body"]');
        var subtotal = doc.querySelector('[data-html="subtotal"]');
        var count = doc.querySelector('[data-html="count"]');
        var discounts = doc.querySelector('[data-html="discounts"]');
        var itemCount = doc.querySelector('[data-html="item_count"]');
        var freeShipping = doc.querySelector('[data-html="free_shipping"]');

        // Swap the item list / empty state.
        if (newBody) self.bodyEl.innerHTML = newBody.innerHTML;

        // Swap the free shipping progress bar.
        if (freeShipping && self.freeShippingEl) self.freeShippingEl.innerHTML = freeShipping.innerHTML;

        // Update the static footer in place (never re-rendered, so the
        // express checkout buttons keep their payment-provider init).
        if (subtotal && self.subtotalEl) self.subtotalEl.innerHTML = subtotal.innerHTML;
        if (count && self.countEl) self.countEl.innerHTML = count.innerHTML;
        if (discounts && self.discountsEl) self.discountsEl.innerHTML = discounts.innerHTML;

        // Toggle empty state (hides the footer when there's nothing to buy).
        if (itemCount) {
          var qty = parseInt(itemCount.textContent, 10) || 0;
          self.root.classList.toggle(self.emptyClass, qty === 0);
        }
      })
      .catch(function () {
        /* network hiccup — leave current contents in place */
      });
  };

  /* ------------------------------------------------------- Nosto integration */

  // After window.Nosto.addProductToCart() completes its /cart/add.js XHR,
  // Nosto calls _targetWindow.refresh_shop_cart (i.e. window.refresh_shop_cart)
  // as its post-add callback. If that function doesn't exist the console shows:
  //   "Uncaught TypeError: _targetWindow.refresh_shop_cart is not a function"
  //
  // We define it here so Nosto can call it, then open/refresh the drawer.
  CartDrawer.prototype._bindNosto = function () {
    var self = this;

    window.refresh_shop_cart = function () {
      if (document.body.classList.contains('template-cart')) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self.internalUpdate = false;
          window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
        })
        .catch(function () { /* leave drawer as-is on network failure */ });
    };
  };

  /* -------------------------------------------------------------------- init */

  function init() {
    var root = document.querySelector(SELECTORS.drawer);
    if (!root) return; // drawer disabled in theme settings
    window.cartDrawer = new CartDrawer(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
